/**
 * Ligne du temps d'une précommande (pain du boulanger) — règle pure.
 *
 * Les clients demandent sans cesse « quand commander le pain ? » (Philippe,
 * 27/09/2026). La réponse vit dans les réglages du type de précommande : jours
 * de fabrication/livraison, délai et heure limite. On la dessine au lieu de
 * l'écrire en dur : si le boulanger change ses jours, le schéma suit.
 *
 * Mode RELATIVE (le pain) : pour chaque jour de cuisson J, commander au plus
 * tard J − `cutoff_offset_days` à `cutoff_hour`, retrait le jour J.
 * Mode FIXED_WEEKLY (viande, poisson) : une seule heure limite par semaine
 * (`cutoff_weekday` à `cutoff_hour`), retrait les jours `pickup_days`.
 */

export interface TypePrecommande {
  name: string;
  slug: string;
  cutoff_mode: 'RELATIVE' | 'FIXED_WEEKLY' | string;
  cutoff_weekday?: number | null;
  cutoff_offset_days?: number | null;
  cutoff_hour: number;
  delivery_days: number[];
  pickup_mode?: string;
  pickup_days: number[];
  /** Produits actifs en précommande de ce type (servi par /shop-config). */
  products_count?: number;
}

export interface EtapeTemps {
  /** Jour de la semaine (0 = dimanche … 6 = samedi). */
  jourLimite: number;
  heureLimite: number;
  /** Jours où l'on vient chercher ce qui a été commandé avant cette limite. */
  joursRetrait: number[];
  /** Jour de cuisson / fabrication (mode RELATIVE), sinon null. */
  jourFabrication: number | null;
}

export interface LigneTemps {
  etapes: EtapeTemps[];
  /** Colonnes à dessiner, dans l'ordre de la semaine à partir de la première limite. */
  jours: number[];
}

export const NOMS_JOURS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];

/** Ordre de la semaine « du lundi » : lundi = 0 … dimanche = 6. */
const rangLundi = (j: number) => (j + 6) % 7;

export function ligneTempsPrecommande(t: TypePrecommande | null | undefined): LigneTemps | null {
  if (!t) return null;
  let etapes: EtapeTemps[] = [];

  if (t.cutoff_mode === 'RELATIVE') {
    const decalage = Math.max(0, Number(t.cutoff_offset_days) || 0);
    const fabrication = (t.delivery_days?.length ? t.delivery_days : t.pickup_days) || [];
    etapes = [...new Set(fabrication)]
      .sort((a, b) => rangLundi(a) - rangLundi(b))
      .map((j) => ({
        jourLimite: (j - decalage + 7 * 7) % 7,
        heureLimite: t.cutoff_hour,
        joursRetrait: [j],
        jourFabrication: j,
      }));
  } else if (t.cutoff_weekday != null) {
    etapes = [{
      jourLimite: t.cutoff_weekday,
      heureLimite: t.cutoff_hour,
      joursRetrait: [...(t.pickup_days || [])].sort((a, b) => rangLundi(a) - rangLundi(b)),
      jourFabrication: null,
    }];
  }
  if (etapes.length === 0) return null;

  // Colonnes : de la première heure limite au dernier jour de retrait, en
  // suivant la semaine (une limite le jeudi et un retrait le samedi → jeu, ven, sam).
  const debut = Math.min(...etapes.map((e) => rangLundi(e.jourLimite)));
  const fin = Math.max(...etapes.flatMap((e) => e.joursRetrait.map((r) => {
    const rr = rangLundi(r);
    return rr >= rangLundi(e.jourLimite) ? rr : rr + 7;
  })));
  const jours: number[] = [];
  for (let r = debut; r <= fin; r++) jours.push((r + 1) % 7);
  return { etapes, jours };
}

export function heureTexte(h: number): string {
  return h === 0 ? 'minuit' : `${h} h`;
}
